if (!customElements.get('facet-filters')) {
  class FacetFilters extends SideDrawer {
    constructor() {
      super();
      
      // Bind methods
      this.handleClick = this.handleClick.bind(this);
      this.handleFilterClickBound = this.handleFilterClick.bind(this);
      this.priceParamKey = 'filter.p.m.custom.first_variant_price';
      
      // Initialize after lazy loading
      window.initLazyScript(this, this.init.bind(this));
      this.isUpdating = false; // Add flag for tracking active request
    }

    connectedCallback() {
      this.init();
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.historyChangeHandler);

      if (this.breakpointChangeHandler) {
        window.removeEventListener('on:breakpoint-change', this.breakpointChangeHandler);
      }

      // Remove event listeners
      if (this.filters) {
        if (this.handleFilterClickWrapper) {
          this.filters.removeEventListener('click', this.handleFilterClickWrapper);
        }
        if (this.handleFilterInputBound) {
          this.filters.removeEventListener('input', this.handleFilterInputBound);
        }
        if (this.handleFiltersClickBound) {
          this.filters.removeEventListener('click', this.handleFiltersClickBound);
        }
        if (this.handleClearAllBound) {
          this.filters.removeEventListener('click', this.handleClearAllBound);
        }
      }

      if (this.desktopSortBy && this.handleSortByChangeBound) {
        this.desktopSortBy.removeEventListener('change', this.handleSortByChangeBound);
      }

      if (this.clearAllButton && this.handleClearAllBound) {
        this.clearAllButton.removeEventListener('click', this.handleClearAllBound);
      }

      // Remove handler when component is destroyed
      const activeFilters = this.querySelector('.active-filters');
      if (activeFilters) {
        activeFilters.removeEventListener('click', this.handleClick);
      }

      const clearAllLink = this.querySelector('.js-clear-all');
      if (clearAllLink) {
        clearAllLink.removeEventListener('click', this.handleClearAll);
      }
    }

    init() {
      this.filteringEnabled = this.dataset.filtering === 'true';
      this.sortingEnabled = this.dataset.sorting === 'true';
      this.form = document.getElementById('facets');
      this.results = document.getElementById('filter-results');

      if (this.filteringEnabled) {
        this.filters = this.querySelector('.facets__filters');
        this.activeFilters = this.querySelector('.facets__active-filters');
        this.activeFiltersList = this.querySelector('.active-filters');
        this.activeFiltersHeader = this.querySelector('.active-filters-header');
        this.footer = this.querySelector('.facets__footer');
        this.clearAllButton = this.querySelector('.js-clear-all'); // Ensure the clear all button has the class .js-clear-all
      }

      if (this.sortingEnabled) {
        this.mobileSortByOptions = this.querySelectorAll('.js-drawer-sort-by');
        this.desktopSortBy = document.querySelector('.products-toolbar__sort');
      }

      // Add handler for filter removal
      const activeFilters = this.querySelector('.active-filters');
      if (activeFilters) {
        activeFilters.addEventListener('click', this.handleClick);
      }

      this.handleBreakpointChange();
      this.addListeners();
      this.handleCloseDrawer();
    }

    addListeners() {
      if (this.filteringEnabled) {
        this.breakpointChangeHandler = this.breakpointChangeHandler || this.handleBreakpointChange.bind(this);
        window.addEventListener('on:breakpoint-change', this.breakpointChangeHandler);

        // Debounced click events for filter checkboxes and buttons
        this.handleFilterClickWrapper = this.handleFilterClickWrapper.bind(this);
        this.filters.addEventListener('click', this.handleFilterClickWrapper);

        // Add click handler for Clear All link
        const clearAllLink = this.querySelector('.js-clear-all');
        if (clearAllLink) {
          clearAllLink.addEventListener('click', (evt) => {
            evt.preventDefault();
            this.handleClearAll(evt);
          });
        }

        // Bind and add listener for 'Clear Filter' buttons
        this.filters.addEventListener('click', this.handleClearFilterBound);

        // Immediate click events for 'js-show-more' buttons
        this.handleFiltersClickBound = this.handleFiltersClick.bind(this);
        this.filters.addEventListener('click', this.handleFiltersClickBound);
        this.filters.addEventListener('input', (event) => {
          if (!event.target.matches('#price-range-number-min, #price-range-number-max')) {
            debounce(this.handleFilterChange.bind(this), 700)(event);
          }
        });

        // Event listener for 'Clear All' button
        if (this.clearAllButton) {
          this.handleClearAllBound = this.handleClearAll.bind(this);
          this.clearAllButton.addEventListener('click', this.handleClearAllBound);
        }

        this.activeFilters.addEventListener('click', this.handleActiveFiltersClick.bind(this));
      }

      if (this.sortingEnabled) {
        this.desktopSortBy.addEventListener('change', this.handleFilterChange.bind(this));
      }

      this.historyChangeHandler = this.historyChangeHandler || this.handleHistoryChange.bind(this);
      window.addEventListener('popstate', this.historyChangeHandler);
    }

    /**
     * Wrapper function for handling filter clicks with debounce, excluding specific elements.
     * @param {object} evt - Event object.
     */
    handleFilterClickWrapper(evt) {
      const target = evt.target;
      const priceMinInput = document.getElementById('price-range-number-min');
      const priceMaxInput = document.getElementById('price-range-number-max');

      // If the event target is within the price range inputs, do not apply debounce
      if (
        (priceMinInput && priceMinInput.contains(target)) ||
        (priceMaxInput && priceMaxInput.contains(target)) ||
        target.matches('#price-range-number-min') ||
        target.matches('#price-range-number-max') ||
        target.matches('.disclosure__toggle') ||
        target.closest('.disclosure__toggle') ||
        target.matches('.filter__toggle') ||
        target.closest('.filter__toggle') ||
        target.matches('.disclosure--price-range') ||
        target.closest('.disclosure--price-range')
      ) {
        // Do nothing here, as these are handled separately
        return;
      }

      // For checkboxes and radio buttons, handle immediately
      if (target.type === 'checkbox' || target.type === 'radio' || 
          target.closest('input[type="checkbox"]') || target.closest('input[type="radio"]')) {
        this.handleFilterClick(evt);
        return;
      }

      // For other elements, apply debounce
      debounce(this.handleFilterClick.bind(this), 700)(evt);
    }

    /**
     * Handles viewport breakpoint changes.
     */
    handleBreakpointChange() {
      if (theme.mediaMatches.lg) {
        this.setAttribute('open', '');
        this.setAttribute('aria-hidden', 'false');
        this.removeAttribute('aria-modal');
        this.removeAttribute('role');

      } else {
        this.close();
        this.setAttribute('role', 'dialog');
        this.setAttribute('aria-modal', 'true');
        this.setAttribute('aria-hidden', 'true');
        this.hidden = false;
      }
    }

    /**
     * Handles filter changes.
     * @param {object} evt - Event object.
     */
    handleFilterChange(evt) {
      const formData = new FormData(this.form);
      const searchParams = new URLSearchParams(formData);
      const emptyParams = [];

      if (this.sortingEnabled) {
        let currentSortBy = searchParams.get('sort_by');

        // Keep the mobile facets form sync'd with the desktop sort by dropdown
        if (evt.target.tagName === 'CUSTOM-SELECT') {
          this.mobileSortByOptions.forEach((option) => {
            option.checked = option.value === evt.detail.selectedValue;
            currentSortBy = evt.detail.selectedValue;
          });
        }

        // Set the 'sort_by' parameter.
        searchParams.set('sort_by', currentSortBy);
      }

      // Get empty parameters
      searchParams.forEach((value, key) => {
        if (!value) emptyParams.push(key);
      });

      // Remove empty parameters
      emptyParams.forEach((key) => {
        searchParams.delete(key);
      });

      this.applyFilters(searchParams.toString(), evt);
    }

    /**
     * Determines if a new filter has been added based on the event.
     * @param {object} evt - Event object.
     * @returns {boolean} - True if a new filter is added, else false.
     */
    isNewFilterAdded(evt) {
      // For simplicity, check if the event type is 'click' and not from price range inputs
      return evt.type === 'click' && !this.isPriceRangeInput(evt);
    }

    /**
     * Determines if the event is from a price range input.
     * @param {object} evt - Event object.
     * @returns {boolean} - True if the event is from a price range input, else false.
     */
    isPriceRangeInput(evt) {
      const target = evt.target;
      return (
        target.matches('#price-range-number-min') ||
        target.matches('#price-range-number-max')
      );
    }

     /**
     * Handles 'click' events on the filters.
     * @param {object} evt - Event object.
     */
     handleFiltersClick(evt) {
      const { target } = evt;

      // Filter 'clear' button clicked.
      if (target.matches('.js-clear-filter')) {
        evt.preventDefault();
        this.applyFilters(new URL(evt.target.href).searchParams.toString(), evt);
      }

      // Filter 'show more' button clicked.
      if (target.matches('.js-show-more')) {
        const filter = target.closest('.filter');
        target.remove();

        filter.querySelectorAll('li').forEach((el) => {
          el.classList.remove('js-hidden');
        });
      }
    }

    /**
     * Handles 'click' events on the active filters.
     * @param {object} evt - Event object.
     */
    handleActiveFiltersClick(evt) {
      if (evt.target.tagName !== 'A') return;
      evt.preventDefault();
      this.applyFilters(new URL(evt.target.href).searchParams.toString(), evt);
    }

    /**
     * Handles history changes (e.g., back button clicked).
     * @param {object} evt - Event object.
     */
    handleHistoryChange(evt) {
      if (evt.state !== null) {
        let searchParams = '';

        if (evt.state && evt.state.searchParams) {
          const params = new URLSearchParams(evt.state.searchParams);
          
          const currentUrl = new URL(window.location.href);
          const currentPrices = currentUrl.searchParams.get('filter.p.m.custom.first_variant_price');
          
          if (currentPrices && !params.has('filter.p.m.custom.first_variant_price')) {
            params.set('filter.p.m.custom.first_variant_price', currentPrices);
          }
          
          searchParams = params.toString();
        }

        this.applyFilters(searchParams, null, false);
      }
    }

    /**
     * Fetches the filtered/sorted page data and updates the current page.
     * @param {string} searchParams - Filter/sort search parameters.
     * @param {object} evt - Event object.
     * @param {boolean} [updateUrl=true] - Update url with the selected options.
     */
    async applyFilters(searchParams, evt, updateUrl = true) {
      // If update is already in progress, skip new request
      if (this.isUpdating) {
        console.log('Update already in progress, skipping...');
        return;
      }

      const customPagination = document.querySelector('custom-pagination');
      if (customPagination) customPagination.dataset.pauseInfiniteScroll = 'true';

      this.isUpdating = true; // Set update flag
      this.results.classList.add('is-loading');

      try {
        const currentUrl = new URL(window.location.href);
        const params = new URLSearchParams(searchParams);
        
        // Get existing and new price values
        const existingPrices = currentUrl.searchParams.get('filter.p.m.custom.first_variant_price');
        const newPrices = params.get('filter.p.m.custom.first_variant_price');

        // If there are new prices and existing ones, combine them
        if (newPrices && existingPrices) {
          const combinedPrices = [...new Set([...existingPrices.split(','), ...newPrices.split(',')])];
          params.set('filter.p.m.custom.first_variant_price', combinedPrices.join(','));
        }
        // If only existing prices and no new ones, preserve existing
        else if (existingPrices && !params.has('filter.p.m.custom.first_variant_price')) {
          params.set('filter.p.m.custom.first_variant_price', existingPrices);
        }

        const fetchUrl = new URL(window.location.pathname, window.location.origin);
        const fetchUrlString = `${fetchUrl.pathname}?${params.toString()}`.replace(/%2C/g, ',');
         
        setTimeout(() => {
          this.handleHideMobileDrawer();
        }, 300);

        const response = await fetch(fetchUrlString);

        if (response.ok) {
          const tmpl = document.createElement('template');
          tmpl.innerHTML = await response.text();

          if (this.filteringEnabled) this.updateFilters(tmpl.content, evt);
          this.results.innerHTML = tmpl.content.getElementById('filter-results').innerHTML;

          if (updateUrl) {
            const newUrl = `${window.location.pathname}?${params.toString()}`.replace(/%2C/g, ',');
            window.history.pushState({ searchParams: params.toString() }, '', newUrl);
          }

          // Add checkbox status updates
          this.updateCheckboxStates();

          // Reinitialize lazyload images after filters applied.
          if (typeof initLazyImages === 'function') initLazyImages();

          // Reinitialize any custom pagination
          if (customPagination && customPagination.reload) customPagination.reload();

          // Scroll to the top of the results if needed
          if (this.results.getBoundingClientRect().top < 0) {
            // If the header is sticky, compensate for it when scrolling to elements
            let headerHeight = 0;
            if (document.querySelector('store-header[data-is-sticky="true"]')) {
              headerHeight = Number.parseInt(
                getComputedStyle(this.parentElement)
                  .getPropertyValue('--header-height')
                  .replace('px', ''),
                10
              ) || 0;
            }
          }

          // Enable the "Show X results" button
          const closeBtn = this.querySelector('.js-close-drawer-mob');
          closeBtn.classList.remove('is-loading');
          closeBtn.removeAttribute('aria-disabled');

          // Renable infinite scroll
          if (customPagination) customPagination.dataset.pauseInfiniteScroll = 'false';

          // Broadcast the update for anything else to hook into
          document.dispatchEvent(new CustomEvent('on:facet-filters:updated'), { bubbles: true });

          // Restore max price placeholder
          const priceRange = document.querySelector('price-range');
          if (priceRange && typeof priceRange.restoreMaxPricePlaceholder === 'function') {
            priceRange.restoreMaxPricePlaceholder();
          }

          
        }
      } catch (error) {
        console.error('Error applying filters:', error);
      } finally {
        this.isUpdating = false; // Скидаємо флаг
        this.results.classList.remove('is-loading');
        if (customPagination) {
          customPagination.dataset.pauseInfiniteScroll = 'false';
        }
      }
    }

    /**
     * Updates the filters with the fetched data.
     * @param {DocumentFragment} html - HTML of the fetched document.
     * @param {object} evt - Event object.
     */
    updateFilters(html, evt) {
      document.querySelectorAll('.filter').forEach((filter) => {
        const { index } = filter.dataset;
        const fetchedFilter = html.querySelector(`.filter[data-index="${index}"]`);

        if (filter.dataset.type !== 'sort') {
          FacetFilters.updateFilter(filter, fetchedFilter, true);
        }
      });

      // Update active filters.
      this.updateActiveFilters(html);

      // Update '[x] results' button (mobile only).
      const footerEl = html.querySelector('.facets__footer');
      if (footerEl) this.footer.innerHTML = footerEl.innerHTML;
    }

    /**
     * Updates a filter.
     * @param {Element} filter - Filter element.
     * @param {Element} fetchedFilter - Fetched filter element.
     * @param {boolean} updateAll - Update all filter markup or just toggle/header.
     */
    static updateFilter(filter, fetchedFilter, updateAll) {
      if (fetchedFilter) {
        filter.hidden = false;
        if (updateAll) {
          filter.innerHTML = fetchedFilter.innerHTML;
        } else {
          // Update toggle and header only.
          filter.replaceChild(
            fetchedFilter.querySelector('.filter__toggle'),
            filter.querySelector('.filter__toggle')
          );
          filter.querySelector('.filter__header').innerHTML =
            fetchedFilter.querySelector('.filter__header').innerHTML;
        }
      } else {
        filter.hidden = true;
      }
    }

    /**
     * Updates the active filters.
     * @param {DocumentFragment} html - HTML of the fetched page.
     */
    updateActiveFilters(html) {
      const activeFiltersList = html.querySelector('.active-filters');
      const activeFiltersHeader = html.querySelector('.active-filters-header');

      if (activeFiltersList) {
        this.activeFiltersList.innerHTML = activeFiltersList.innerHTML;
      }
      if (activeFiltersHeader) {
        this.activeFiltersHeader.innerHTML = activeFiltersHeader.innerHTML;
      }

      // Check for active filters or price range
      const hasActiveFilters = this.activeFiltersList.querySelector('.active-filter') || 
                              document.querySelector('price-range')?.priceState?.min || 
                              document.querySelector('price-range')?.priceState?.max;
                              
      this.activeFilters.hidden = !hasActiveFilters;
    }

    /**
     * Handles the "Clear All" button click.
     * @param {object} evt - Event object.
     */
    handleClearAll(evt) {
      evt.preventDefault();

      // Clear all form controls
      if (this.form && this.form.controls) {
        Object.keys(this.form.controls).forEach((key) => {
          const control = this.form.get(key);
          if (control) {
            control.reset();
          }
        });
      }

      // Clear price range inputs і стану
      const priceRange = document.querySelector('price-range');
      if (priceRange) {
        const minInput = priceRange.querySelector('#price-range-number-min');
        const maxInput = priceRange.querySelector('#price-range-number-max');
        if (minInput) minInput.value = '';
        if (maxInput) maxInput.value = '';
        
        priceRange.priceState = { min: '', max: '' };
      }

      // Create clean URL without any parameters
      const cleanUrl = window.location.pathname;
      const params = new URLSearchParams();

      // Preserve only sort_by parameter if needed
      if (this.sortingEnabled) {
        const currentUrl = new URL(window.location.href);
        if (currentUrl.searchParams.has('sort_by')) {
          params.set('sort_by', currentUrl.searchParams.get('sort_by'));
        }
      }

      // Update URL and reload results
      window.history.pushState({ searchParams: params.toString() }, '', cleanUrl);
      this.applyFilters('', evt);

      // Update active filters display
      const activeFilters = this.querySelector('.facets__active-filters');
      if (activeFilters) {
        activeFilters.hidden = true;
      }

      this.handleHideMobileDrawer();
    }

    /**
     * Updates the url with the current filter/sort parameters.
     * @param {string} searchParams - Filter/sort parameters.
     */
    static async updateURL(searchParams) {
      const url = new URL(window.location);
      const instance = document.querySelector('facet-filters');
      
      // Clear all parameters
      url.searchParams.forEach((value, key) => {
        url.searchParams.delete(key);
      });

      if (searchParams) {
        const params = new URLSearchParams(searchParams);
        
        // Add all parameters
        params.forEach((value, key) => {
          url.searchParams.set(key, value);
        });
      }

      const newUrl = `${window.location.pathname}?${url.searchParams.toString()}`.replace(/%2C/g, ',');
      
      window.history.pushState(
        { searchParams: url.searchParams.toString() },
        '',
        newUrl
      );
    }

    handleClick(evt) {
      evt.preventDefault();
      const target = evt.target.closest('a');
      if (!target) return;

      // If update is already in progress, wait for it to complete
      if (this.isUpdating) {
        const waitForUpdate = async () => {
          await new Promise(resolve => {
            const checkInterval = setInterval(() => {
              if (!this.isUpdating) {
                clearInterval(checkInterval);
                resolve();
              }
            }, 100);
          });

          // After previous update is complete, perform removal
          this.removeFilter(target);
        };

        waitForUpdate();
        return;
      }

      this.removeFilter(target);
    }

    async removeFilter(target) {
      const filterType = target.dataset.filterType;
      const params = target.dataset.params;
      
      if (!params) return;

      try {
        // Decode URL parameters
        const searchParams = new URLSearchParams(window.location.search);
        const targetParams = new URLSearchParams(params.split('?')[1]);

        // Remove parameters based on filter type
        if (filterType === 'price') {
          // Remove all price related parameters
          ['filter.p.m.custom.first_variant_price', 'filter.p.m.custom.price_min', 'filter.p.m.custom.price_max'].forEach(param => {
            searchParams.delete(param);
          });

          // Reset price range component if exists
          const priceRange = document.querySelector('price-range');
          if (priceRange) {
            priceRange.priceState = { min: '', max: '' };
            const minInput = priceRange.querySelector('#price-range-number-min');
            const maxInput = priceRange.querySelector('#price-range-number-max');
            if (minInput) minInput.value = '';
            if (maxInput) maxInput.value = '';
          }
        } else {
          // Remove specific filter parameter
          const key = target.dataset.key;
          const value = target.dataset.value;
          
          if (key) {
            // If we have both key and value, remove specific value
            if (value) {
              const currentValues = searchParams.getAll(key);
              searchParams.delete(key);
              currentValues.forEach(val => {
                if (val !== value) {
                  searchParams.append(key, val);
                }
              });
            } else {
              // If we only have key, remove all values for this key
              searchParams.delete(key);
            }
          }
        }

        // Create clean URL
        const cleanUrl = `${window.location.pathname}${searchParams.toString() ? '?' + searchParams.toString() : ''}`;
        
        // Update URL
        window.history.pushState({ searchParams: searchParams.toString() }, '', cleanUrl);

        // Apply filters
        await this.applyFilters(searchParams.toString());

        // Remove filter from display
        target.remove();

        // Check if we need to hide active filters container
        const activeFilters = this.querySelector('.facets__active-filters');
        if (activeFilters) {
          const remainingFilters = activeFilters.querySelectorAll('.active-filter:not(.hidden)');
          activeFilters.hidden = remainingFilters.length === 0;
        }

        this.handleHideMobileDrawer();

      } catch (error) {
        console.error('Error removing filter:', error);
      }
    }

    // New method for getting current prices
    getCurrentPrices() {
      const url = new URL(window.location.href);
      return url.searchParams.get(this.priceParamKey);
    }

    // New method for formatting price parameters
    formatPricesParam(prices) {
      if (!prices) return '';
      return prices.split(',')
        .map(price => Number(parseFloat(price)).toFixed(2))
        .join(',');
    }

    // New method for creating URLSearchParams with price preservation
    async createParamsWithPrices(searchParams) {
      const params = new URLSearchParams(searchParams);
      
      // Get min and max prices
      const minPrice = params.get('filter.p.m.custom.price_min');
      const maxPrice = params.get('filter.p.m.custom.price_max');

      // If price range exists, get corresponding prices
      if (minPrice && maxPrice) {
        const pricesInRange = await this.getPricesInRange(minPrice, maxPrice);
        if (pricesInRange) {
          params.set(this.priceParamKey, pricesInRange);
        }
      }

      return params;
    }

    // New method for getting prices within range
    async getPricesInRange(minPrice, maxPrice) {
      if (!minPrice || !maxPrice) return '';
      
      // Get all prices from window.firstVariantPrices
      const allPrices = window.firstVariantPrices || [];
      
      // Filter prices within range
      const pricesInRange = allPrices.filter(price => {
        const numericPrice = parseFloat(price);
        return numericPrice >= parseFloat(minPrice) && numericPrice <= parseFloat(maxPrice);
      });

      // Format prices
      return pricesInRange
        .map(price => Number(parseFloat(price)).toFixed(2))
        .join(',');
    }

    handleHideMobileDrawer() {
      if(window.innerWidth < 768) {
          const facetFilters = document.querySelector('facet-filters');
          if (facetFilters) {
            facetFilters.removeAttribute('open');
          }

          const overlay = document.querySelectorAll('.overlay');
          if (overlay) {
            overlay.forEach(item => {
              item.classList.remove('is-visible');
            });
          }

          const body = document.body;
          body.style.position = '';
          body.style.overflow = '';
          body.classList.remove('fixed');
      }
    }


    handleCloseDrawer() {
      const closeBtn = this.querySelector('.js-close-drawer');
      if (closeBtn) {
        closeBtn.addEventListener('click', () => {
          this.handleHideMobileDrawer();
        });
      }

      // Add handler for mobile close button
      const closeBtnMobile = this.querySelector('.js-close-drawer-mob');
      if (closeBtnMobile) {
        closeBtnMobile.addEventListener('click', () => {
          this.handleHideMobileDrawer();
        });
      }
    }

    handleFilterClick(evt) {
      const target = evt.target;
      
      // Skip if target is a special element
      if (
        target.matches('.disclosure__toggle') ||
        target.closest('.disclosure__toggle') ||
        target.matches('.filter__toggle') ||
        target.closest('.filter__toggle') ||
        target.matches('.disclosure--price-range') ||
        target.closest('.disclosure--price-range')
      ) {
        return;
      }

      // Handle checkbox clicks
      if (target.type === 'checkbox' || target.closest('input[type="checkbox"]')) {
        this.handleFilterChange(evt);
        return;
      }

      // Handle radio button clicks (for sorting)
      if (target.type === 'radio' || target.closest('input[type="radio"]')) {
        this.handleFilterChange(evt);
        return;
      }

      // Handle label clicks
      const label = target.closest('label');
      if (label) {
        const input = document.getElementById(label.getAttribute('for'));
        if (input && (input.type === 'checkbox' || input.type === 'radio')) {
          input.checked = !input.checked;
          this.handleFilterChange(evt);
          return;
        }
      }
    }

    updateCheckboxStates() {
      const url = new URL(window.location.href);
      const currentSortBy = url.searchParams.get('sort_by');

      const sortRadios = this.querySelectorAll('input[type="radio"].js-drawer-sort-by');
      sortRadios.forEach(radio => {
        radio.checked = radio.value === currentSortBy;
      });

      const checkboxes = this.querySelectorAll('input[type="checkbox"]');
      checkboxes.forEach(checkbox => {
        const name = checkbox.getAttribute('name');
        const value = checkbox.value;
        const values = url.searchParams.getAll(name);
        checkbox.checked = values.includes(value);
      });
    }
  }

  customElements.define('facet-filters', FacetFilters);
}